import React, { useState, useRef, useEffect } from 'react';
// @ts-ignore
import dropdownIcon from '../../image-assets/icon.arrows.dropdown.svg';
// @ts-ignore
import checkIcon from '../../image-assets/icon.checkmark.svg';
// @ts-ignore
import languagesData from '../../data/languages.json';
import './LanguageSelector.css';

interface LanguageSelectorProps {
  languages: string[];
  selectedLanguage?: string;
  isOwner?: boolean;
  onLanguageClick?: (language: string) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  languages,
  selectedLanguage,
  isOwner = false,
  onLanguageClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      // When closing, wait for animation to complete before removing from DOM
      const menu = menuRef.current;
      if (menu) {
        menu.style.animation = 'dropdownClose 0.12s ease-in forwards';
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, 120); // Match animation duration
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen]);

  const handleLanguageClick = (language: string, isFirstItem: boolean) => {
    if (!isFirstItem) {
      onLanguageClick?.(language);
      setIsOpen(false);
    }
  };

  // Create maps for language codes and names, preserving original case
  const languageData = languagesData.languages.reduce((acc: { [key: string]: { name: string, originalCode: string } }, lang: { code: string, name: string }) => {
    // Store both hyphen and underscore versions to handle both formats
    const normalizedCode = lang.code.toLowerCase();
    acc[normalizedCode] = { name: lang.name, originalCode: lang.code };
    // Also store the alternative format (if it contains a hyphen or underscore)
    if (normalizedCode.includes('-')) {
      acc[normalizedCode.replace(/-/g, '_')] = { name: lang.name, originalCode: lang.code };
    } else if (normalizedCode.includes('_')) {
      acc[normalizedCode.replace(/_/g, '-')] = { name: lang.name, originalCode: lang.code };
    }
    return acc;
  }, {});

  // Debug missing languages
  languages.forEach(langCode => {
    if (!languageData[langCode.toLowerCase()]) {
      console.warn('Missing language data for:', langCode);
    }
  });

  const getLanguageDisplay = (langCode: string) => {
    const langInfo = languageData[langCode.toLowerCase()];
    return langInfo || { name: langCode, originalCode: langCode };
  };

  return (
    <div className={`language-selector ${isOpen ? 'open' : ''} ${isOwner ? 'is-owner' : ''}`}>
      <div 
        className="language-selector-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="selected-language">
          {selectedLanguage ? (
            <>
              <span className="language-name">{getLanguageDisplay(selectedLanguage).name}</span>
              <span className="language-code">{getLanguageDisplay(selectedLanguage).originalCode}</span>
            </>
          ) : (
            'Select Language'
          )}
        </div>
        <img 
          src={dropdownIcon} 
          alt="Toggle" 
          className={`chevron-icon ${isOpen ? 'open' : ''}`}
        />
      </div>
      {isVisible && (
        <div ref={menuRef} className="language-dropdown">
          <div className="language-list">
            {languages.length === 0 ? (
              <div className="empty-state">No languages added</div>
            ) : (
              languages.map((langCode, index) => {
                const langInfo = getLanguageDisplay(langCode);
                const isFirstItem = index === 0;
                return (
                  <div
                    key={langCode}
                    className={`language-item ${langCode === selectedLanguage ? 'selected' : ''} ${isFirstItem ? 'locked' : ''}`}
                    onClick={() => handleLanguageClick(langCode, isFirstItem)}
                  >
                    <div className="language-info">
                      <span className="language-name">{langInfo.name}</span>
                      <span className="language-code">{langInfo.originalCode}</span>
                    </div>
                    <div className="language-actions">
                      {isFirstItem ? (
                        <span className="source-label">Source</span>
                      ) : langCode === selectedLanguage && (
                        <img
                          src={checkIcon}
                          alt="Selected"
                          className="check-icon"
                        />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;